import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Loader2,
  CheckCircle,
  XCircle,
  ArrowLeft,
  ArrowRight,
  Wallet,
  CreditCard,
  ShieldCheck,
  Package,
} from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { useTranslation } from "react-i18next";
import { registerKitService } from "@/api/services/registerKitService";
import apiClient from "@/api/client";
import { formatDecimalString } from "@/utils/formatPrice";

// ─── Types ───────────────────────────────────────────────────────────────
type KitTier = "basic" | "advanced" | "premium";
type PaymentMethod = "wallet" | "flutterwave";

// KX-33: the tiers endpoint now resolves a fee per deployment country. The
// backend owns every amount; we never compute a price client-side. The `fee`
// object carries exact minor units AND decimal display strings — we only format
// those for locale display. `fee` is null when no fee is configured for that
// country + hardware class, in which case the tier cannot be selected.
interface TierFee {
  chargeable: boolean;
  currency: string;
  fee: string; // decimal string, e.g. "10000.00" (no symbol/grouping)
  tax: string; // decimal string
  total: string; // decimal string
  fee_minor: string; // exact integer minor units
  tax_minor: string;
  gross_minor: string;
  rate_bps?: number;
  tax_inclusive?: boolean;
  hardware_class?: string;
  country_code?: string;
}

interface TierInfo {
  label: string;
  description?: string;
  hardware_class?: string;
  fee: TierFee | null;
}

type TiersResponse = Record<KitTier, TierInfo>;

interface WalletBalance {
  balance: number;
  currency: string;
}


interface WalletApiResponse {
  data?: {
    balance?: number | string;
    currency?: string;
    available?: number | string;
    amount?: number | string;
    walletBalance?: number | string;
    cashBalance?: number | string;
    usdBalance?: number | string;
    wallet?: {
      balance?: number | string;
      currency?: string;
    };
  };
  balance?: number | string;
  currency?: string;
}

// Defensive wallet-balance extractor.
// /wallet/balance returns different shapes across the codebase; this walks
// the response and picks the first sensible numeric balance + currency.
// Returns null if no recognizable balance found.
function extractWalletBalance(resp: WalletApiResponse | null | undefined): WalletBalance | null {
  if (!resp) return null;

  // Try a series of candidate paths in priority order.
  const candidatePaths: Array<{ value: unknown; currency?: unknown }> = [
    // Common API shapes
    { value: resp?.data?.balance, currency: resp?.data?.currency },
    { value: resp?.balance, currency: resp?.currency },
    { value: resp?.data?.available, currency: resp?.data?.currency },
    { value: resp?.data?.amount, currency: resp?.data?.currency },
    { value: resp?.data?.walletBalance, currency: resp?.data?.currency },
    { value: resp?.data?.cashBalance, currency: resp?.data?.currency || "USD" },
    { value: resp?.data?.usdBalance, currency: "USD" },
    // Nested under data.wallet
    { value: resp?.data?.wallet?.balance, currency: resp?.data?.wallet?.currency },
    // Last-resort: any numeric field on data
  ];

  for (const c of candidatePaths) {
    const num = typeof c.value === "number" ? c.value : Number(c.value);
    if (!isNaN(num) && c.value !== undefined && c.value !== null && c.value !== "") {
      return {
        balance: num,
        currency: typeof c.currency === "string" && c.currency ? c.currency : "USD",
      };
    }
  }

  return null;
}

// ─── Component ────────────────────────────────────────────────────────────
export default function HotspotRegistrationStep3() {
  const { theme } = useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── State ─────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [tiers, setTiers] = useState<TiersResponse | null>(null);
  const [tiersLoading, setTiersLoading] = useState(true);
  // Deployment country resolved by the tiers endpoint (display only).
  const [countryCode, setCountryCode] = useState<string | null>(null);

  const [walletBalance, setWalletBalance] = useState<WalletBalance | null>(null);

  const [selectedTier, setSelectedTier] = useState<KitTier>("basic");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("wallet");

  // ── Helpers ───────────────────────────────────────────────────────────
  const showMessage = (type: "success" | "error", text: string) => {
    setMessage({ type, text });
    if (type === "error") {
      setTimeout(() => setMessage(null), 8000);
    }
  };

  // ── Load tiers + wallet balance on mount ──────────────────────────────
  useEffect(() => {
    const init = async () => {
      // Session guard
      const registrationId = sessionStorage.getItem("registrationId");
      if (!registrationId && !searchParams.get("tx_ref")) {
        // Allow Flutterwave return even if session lost (we re-read registrationId from URL)
        showMessage(
          "error",
          t("operator.registerKit.step3.errors.sessionLost", {
            defaultValue: "No registration session found. Redirecting...",
          })
        );
        setTimeout(() => navigate("/operator/registerKit"), 2000);
        return;
      }

      // Load tiers scoped to the node's deployment country so the fee resolves
      // correctly. Prefer the registrationId (backend derives the country); fall
      // back to an explicit ?country=XX if present on the URL.
      try {
        const params = new URLSearchParams();
        if (registrationId) params.set("registrationId", registrationId);
        const countryParam = searchParams.get("country");
        if (countryParam) params.set("country", countryParam);
        const qs = params.toString();
        const tiersResp = await apiClient.get<{
          success: boolean;
          data: TiersResponse;
          country_code?: string;
        }>(`/registerkit/tiers${qs ? `?${qs}` : ""}`);
        if (tiersResp.data?.success && tiersResp.data?.data) {
          setTiers(tiersResp.data.data);
          if (tiersResp.data.country_code) setCountryCode(tiersResp.data.country_code);
        }
      } catch (err: unknown) {
        console.error("Failed to load tiers:", err);
        showMessage(
          "error",
          t("operator.registerKit.step3.errors.tiersFailed", {
            defaultValue: "Could not load kit tiers. Refresh to try again.",
          })
        );
      } finally {
        setTiersLoading(false);
      }

      // Load wallet balance (silent — used to decide if wallet method is viable)
      try {
        const walletResp = await apiClient.get<WalletApiResponse>("/wallet/balance");
        const parsed = extractWalletBalance(walletResp?.data);
        if (parsed) {
          setWalletBalance(parsed);
        }
      } catch {
        // Don't show an error — wallet balance is just a hint, not required
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handle Flutterwave return ─────────────────────────────────────────
  useEffect(() => {
    const txRef = searchParams.get("tx_ref");
    const transactionId = searchParams.get("transaction_id");
    const flwStatus = searchParams.get("status"); // 'successful' | 'cancelled' | 'failed'

    if (!txRef) return;

    const verify = async () => {
      setVerifying(true);
      try {
        // If user cancelled at Flutterwave, don't even hit verify — go back
        if (flwStatus === "cancelled") {
          showMessage(
            "error",
            t("operator.registerKit.step3.errors.paymentCancelled", {
              defaultValue: "Payment was cancelled. You can try again.",
            })
          );
          setVerifying(false);
          // Strip URL params
          window.history.replaceState({}, "", "/operator/registerKitStep3");
          return;
        }

        const params = new URLSearchParams();
        params.set("tx_ref", txRef);
        if (transactionId) params.set("transaction_id", transactionId);
        if (flwStatus) params.set("status", flwStatus);

        const verifyResp = await apiClient.get<{ success: boolean; message?: string }>(
          `/registerkit/activation/flutterwave/verify?${params.toString()}`
        );

        if (!verifyResp.data?.success) {
          throw new Error(verifyResp.data?.message || "Verification failed");
        }

        // Payment verified — now submit for approval
        const registrationId = sessionStorage.getItem("registrationId");
        if (!registrationId) {
          // Verification succeeded but we lost session — page reload would have done it
          // Send them to the Setup Guide; it'll show the correct state anyway.
          showMessage(
            "success",
            t("operator.registerKit.step3.successApprovalAuto", {
              defaultValue: "Payment confirmed. Your kit has been submitted.",
            })
          );
          setTimeout(() => navigate("/operator/setupguide"), 1500);
          return;
        }

        await registerKitService.submitForApproval(registrationId);
        sessionStorage.removeItem("registrationId");

        showMessage(
          "success",
          t("operator.registerKit.step3.success", {
            defaultValue: "Payment received! Your kit is being activated.",
          })
        );
        setTimeout(() => navigate("/operator/setupguide"), 2000);
      } catch (err: unknown) {
        const errObj = err as {response?: {data?: {message?: string}}, message?: string};
        const apiMsg = errObj?.response?.data?.message || errObj?.message;
        showMessage(
          "error",
          apiMsg ||
            t("operator.registerKit.step3.errors.verifyFailed", {
              defaultValue: "We couldn't verify your payment. Contact support if charged.",
            })
        );
        // Strip URL params so retry works
        window.history.replaceState({}, "", "/operator/registerKitStep3");
      } finally {
        setVerifying(false);
      }
    };

    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (submitting || loading || verifying) return;

    const registrationId = sessionStorage.getItem("registrationId");
    if (!registrationId) {
      showMessage(
        "error",
        t("operator.registerKit.step3.errors.sessionLost", {
          defaultValue: "No registration session found. Redirecting...",
        })
      );
      setTimeout(() => navigate("/operator/registerKit"), 2000);
      return;
    }

    if (!tiers) {
      return showMessage(
        "error",
        t("operator.registerKit.step3.errors.tiersFailed", {
          defaultValue: "Kit tiers not loaded. Refresh and try again.",
        })
      );
    }

    const tier = tiers[selectedTier];
    if (!tier) {
      return showMessage(
        "error",
        t("operator.registerKit.step3.errors.tierMissing", {
          defaultValue: "Selected tier not found. Pick another.",
        })
      );
    }
    // Guard: a tier with no resolved fee cannot be paid for.
    if (!tier.fee) {
      return showMessage(
        "error",
        t("operator.registerKit.step3.errors.tierUnavailable", {
          defaultValue: "This tier isn't available in your country. Pick another.",
        })
      );
    }

    setSubmitting(true);
    setLoading(true);

    try {
      if (paymentMethod === "wallet") {
        // Pay from wallet balance
        const payload = {
          registrationId,
          kitTier: selectedTier,
        };
        const walletResp = await apiClient.post<{ success: boolean; message?: string }>(
          "/registerkit/activation/wallet",
          payload
        );

        if (!walletResp.data?.success) {
          throw new Error(walletResp.data?.message || "Wallet payment failed");
        }

        // Payment cleared — submit. KX-39: activation auto-approves on a clean
        // signal (valid MAC, payment cleared, device reachable, not blocklisted),
        // so the operator goes live right away; manual review only on a risk flag.
        await registerKitService.submitForApproval(registrationId);
        sessionStorage.removeItem("registrationId");

        showMessage(
          "success",
          t("operator.registerKit.step3.success", {
            defaultValue: "Payment received! Your kit is being activated.",
          })
        );
        setTimeout(() => navigate("/operator/setupguide"), 2000);
      } else {
        // Pay with Flutterwave (card)
        const payload = {
          registrationId,
          kitTier: selectedTier,
        };
        const initResp = await apiClient.post<{
          success: boolean;
          message?: string;
          data?: { paymentUrl?: string; link?: string; paymentLink?: string };
        }>("/registerkit/activation/flutterwave/init", payload);

        if (!initResp.data?.success) {
          throw new Error(initResp.data?.message || "Could not start card payment");
        }

        const paymentUrl = initResp.data.data?.paymentLink || initResp.data.data?.paymentUrl || initResp.data.data?.link;
        if (!paymentUrl) {
          throw new Error("Payment URL missing from response");
        }

        // Off to Flutterwave. They'll redirect back with ?tx_ref&transaction_id&status,
        // and the useEffect above handles verification.
        window.location.href = paymentUrl;
        return; // Don't reset loading — we're navigating away
      }
    } catch (error: unknown) {
      const errObj = error as {response?: {data?: {message?: string}, status?: number}, message?: string};
      const apiMsg = errObj?.response?.data?.message;
      const statusCode = errObj?.response?.status;
      let errorMsg =
        apiMsg ||
        errObj?.message ||
        t("operator.registerKit.step3.errors.submitFailed", {
          defaultValue: "Could not process your payment. Please try again.",
        });
      if (statusCode === 401) {
        errorMsg = t("operator.registerKit.step3.errors.unauthorized", {
          defaultValue: "Your session expired. Please sign in again.",
        });
        setTimeout(() => navigate("/signin"), 2000);
      } else if (statusCode === 402) {
        // Insufficient wallet balance (common case)
        errorMsg =
          apiMsg ||
          t("operator.registerKit.step3.errors.insufficientFunds", {
            defaultValue: "Insufficient wallet balance. Try paying with card instead.",
          });
      } else if (statusCode === 404) {
        errorMsg = t("operator.registerKit.step3.errors.notFound", {
          defaultValue: "Registration not found. Please start over.",
        });
        sessionStorage.removeItem("registrationId");
        setTimeout(() => navigate("/operator/registerKit"), 2500);
      }
      showMessage("error", errorMsg);
    } finally {
      setLoading(false);
      setTimeout(() => setSubmitting(false), 1000);
    }
  };

  // If the default tier isn't available in this country, move selection to the
  // first tier that has a resolved fee so the user isn't stranded on an empty
  // summary.
  useEffect(() => {
    if (!tiers) return;
    const order: KitTier[] = ["basic", "advanced", "premium"];
    if (tiers[selectedTier]?.fee) return;
    const firstAvailable = order.find((k) => tiers[k]?.fee);
    if (firstAvailable) setSelectedTier(firstAvailable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiers]);

  // ── Derived UI helpers ────────────────────────────────────────────────
  const selectedTierInfo = tiers?.[selectedTier];
  // Compare the wallet balance against the backend-provided fee total. We parse
  // the backend's exact decimal string — no client-side price math.
  const selectedFeeTotal = selectedTierInfo?.fee ? Number(selectedTierInfo.fee.total) : null;
  const walletShortfall =
    paymentMethod === "wallet" &&
    walletBalance &&
    typeof walletBalance.balance === "number" &&
    selectedFeeTotal !== null &&
    Number.isFinite(selectedFeeTotal)
      ? walletBalance.balance < selectedFeeTotal
      : false;

  const tierOrder: KitTier[] = ["basic", "advanced", "premium"];

  // A tier is selectable only when the backend resolved a fee for it.
  const isTierAvailable = (tier?: TierInfo | null): boolean => !!tier && !!tier.fee;

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>
          {t("operator.registerKit.step3.metaTitle", {
            defaultValue: "Step 3: Activation Fee | KonectX",
          })}
        </title>
        <meta
          name="description"
          content={t("operator.registerKit.step3.metaDescription", {
            defaultValue: "Pay your kit activation fee to complete registration.",
          })}
        />
      </Helmet>

      <div className={`min-h-screen ${theme === "dark" ? "bg-black" : "bg-gray-50"}`}>
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-8 sm:py-12">

          {/* Verifying overlay */}
          {verifying && (
            <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              theme === "dark" ? "bg-blue-900/20 text-blue-300 border border-blue-800" : "bg-blue-50 text-blue-800 border border-blue-200"
            }`}>
              <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" />
              <span className="text-sm font-medium">
                {t("operator.registerKit.step3.verifying", {
                  defaultValue: "Verifying your payment...",
                })}
              </span>
            </div>
          )}

          {/* Message toast */}
          {message && (
            <div
              className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
                message.type === "success"
                  ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800"
                  : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"
              }`}
            >
              {message.type === "success" ? (
                <CheckCircle size={18} className="flex-shrink-0" />
              ) : (
                <XCircle size={18} className="flex-shrink-0" />
              )}
              <span className="text-sm font-medium">{message.text}</span>
            </div>
          )}

          {/* Header + progress */}
          <div className="mb-10">
            <h1 className={`text-4xl font-bold mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              {t("operator.registerKit.step3.heading", { defaultValue: "Pay Activation Fee" })}
            </h1>
            <p className={`mb-6 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
              {t("operator.registerKit.step3.subtitle", {
                defaultValue: "Step 3 of 3 • One-time activation fee to bring your kit online.",
              })}
            </p>

            {/* Progress bar (same pattern as the old file) */}
            <div className="flex items-center justify-between mb-3 px-1">
              <span className={`text-sm font-bold uppercase tracking-wider ${theme === "dark" ? "text-gray-600" : "text-gray-400"}`}>
                {t("operator.registerKit.progress.step1", { defaultValue: "Step 1: Device" })}
              </span>
              <span className={`text-sm font-bold uppercase tracking-wider ${theme === "dark" ? "text-gray-600" : "text-gray-400"}`}>
                {t("operator.registerKit.progress.step2", { defaultValue: "Step 2: Connectivity" })}
              </span>
              <span className="text-sm font-bold text-[#2E5A88] uppercase tracking-wider">
                {t("operator.registerKit.progress.step3", { defaultValue: "Step 3: Payment" })}
              </span>
            </div>
            <div className={`w-full rounded-full h-2 ${theme === "dark" ? "bg-gray-800" : "bg-gray-200"}`}>
              <div className="bg-[#2E5A88] h-2 rounded-full transition-all duration-500" style={{ width: "100%" }} />
            </div>
          </div>

          {/* Info banner */}
          <div className={`mb-8 rounded-xl p-4 flex gap-3 ${theme === "dark" ? "bg-blue-900/20 border border-blue-800" : "bg-blue-50 border border-blue-100"}`}>
            <ShieldCheck className="w-5 h-5 text-[#2E5A88] flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className={`font-semibold mb-1 ${theme === "dark" ? "text-blue-300" : "text-blue-900"}`}>
                {t("operator.registerKit.step3.info.title", { defaultValue: "One-time activation fee" })}
              </p>
              <p className={`${theme === "dark" ? "text-blue-200" : "text-blue-800"}`}>
                {t("operator.registerKit.step3.info.body", {
                  defaultValue:
                    "Choose your kit tier and pay the activation fee. Once paid, your kit is submitted for admin approval and you'll start earning KXT tokens from end-user data plans.",
                })}
              </p>
            </div>
          </div>

          {/* Tier picker */}
          <div className="mb-8">
            <label className={`block text-sm font-bold uppercase tracking-wider mb-3 ${theme === "dark" ? "text-gray-400" : "text-gray-700"}`}>
              {t("operator.registerKit.step3.tierLabel", { defaultValue: "Choose your kit tier" })}
            </label>

            {tiersLoading ? (
              <div className={`rounded-xl border p-8 flex items-center justify-center ${theme === "dark" ? "bg-[#111] border-gray-800" : "bg-white border-gray-200"}`}>
                <Loader2 className="w-6 h-6 animate-spin text-[#2E5A88]" />
              </div>
            ) : !tiers ? (
              <div className={`rounded-xl border p-6 text-sm ${theme === "dark" ? "bg-red-900/20 border-red-800 text-red-300" : "bg-red-50 border-red-200 text-red-700"}`}>
                {t("operator.registerKit.step3.errors.tiersFailed", { defaultValue: "Could not load kit tiers. Refresh to try again." })}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {tierOrder.map((tierKey) => {
                  const tier = tiers[tierKey];
                  if (!tier) return null;
                  const available = isTierAvailable(tier);
                  const isSelected = selectedTier === tierKey;
                  return (
                    <button
                      key={tierKey}
                      type="button"
                      onClick={() => available && setSelectedTier(tierKey)}
                      disabled={loading || submitting || verifying || !available}
                      aria-disabled={!available}
                      className={`rounded-xl border-2 px-4 py-5 text-left transition-colors disabled:cursor-not-allowed ${
                        !available
                          ? theme === "dark"
                            ? "border-gray-800 bg-[#0c0c0c] text-gray-600 opacity-60"
                            : "border-gray-200 bg-gray-50 text-gray-400 opacity-70"
                          : isSelected
                            ? "border-[#2E5A88] bg-[#2E5A88] text-white"
                            : theme === "dark"
                              ? "border-gray-800 bg-[#111] text-gray-300 hover:border-gray-700"
                              : "border-gray-300 bg-white text-gray-700 hover:border-[#2E5A88]"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="w-5 h-5" />
                        <span className="text-sm font-bold uppercase tracking-wider">
                          {tier.label || tierKey}
                        </span>
                      </div>
                      {available && tier.fee ? (
                        <div className="text-2xl font-bold mb-1">
                          {formatDecimalString(tier.fee.total, tier.fee.currency)}
                        </div>
                      ) : (
                        <div className="text-sm font-medium mb-1">
                          {t("operator.registerKit.step3.tierUnavailable", {
                            defaultValue: "Not available in your country",
                          })}
                        </div>
                      )}
                      {tier.description && (
                        <p className={`text-xs ${isSelected && available ? "text-blue-100" : theme === "dark" ? "text-gray-500" : "text-gray-500"}`}>
                          {tier.description}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Payment method picker */}
          {tiers && (
            <div className="mb-8">
              <label className={`block text-sm font-bold uppercase tracking-wider mb-3 ${theme === "dark" ? "text-gray-400" : "text-gray-700"}`}>
                {t("operator.registerKit.step3.payWith", { defaultValue: "How do you want to pay?" })}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentMethod("wallet")}
                  disabled={loading || submitting || verifying}
                  className={`rounded-xl border-2 px-4 py-4 text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
                    paymentMethod === "wallet"
                      ? "border-[#2E5A88] bg-[#2E5A88] text-white"
                      : theme === "dark"
                        ? "border-gray-800 bg-[#111] text-gray-300 hover:border-gray-700"
                        : "border-gray-300 bg-white text-gray-700 hover:border-[#2E5A88]"
                  }`}
                >
                  <Wallet className="w-5 h-5" />
                  {t("operator.registerKit.step3.methods.wallet", { defaultValue: "Wallet Balance" })}
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod("flutterwave")}
                  disabled={loading || submitting || verifying}
                  className={`rounded-xl border-2 px-4 py-4 text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
                    paymentMethod === "flutterwave"
                      ? "border-[#2E5A88] bg-[#2E5A88] text-white"
                      : theme === "dark"
                        ? "border-gray-800 bg-[#111] text-gray-300 hover:border-gray-700"
                        : "border-gray-300 bg-white text-gray-700 hover:border-[#2E5A88]"
                  }`}
                >
                  <CreditCard className="w-5 h-5" />
                  {t("operator.registerKit.step3.methods.card", { defaultValue: "Card (Flutterwave)" })}
                </button>
              </div>

              {/* Wallet balance hint */}
              {paymentMethod === "wallet" && walletBalance && typeof walletBalance.balance === "number" && selectedTierInfo?.fee && (
                <div className={`mt-3 px-4 py-3 rounded-lg text-sm ${
                  walletShortfall
                    ? theme === "dark" ? "bg-amber-900/20 border border-amber-800 text-amber-300" : "bg-amber-50 border border-amber-200 text-amber-800"
                    : theme === "dark" ? "bg-[#111] border border-gray-800 text-gray-300" : "bg-gray-50 border border-gray-200 text-gray-700"
                }`}>
                  <div className="flex justify-between items-center">
                    <span>{t("operator.registerKit.step3.walletBalance", { defaultValue: "Wallet balance" })}:</span>
                    <span className="font-mono font-semibold">
                      {walletBalance.currency === "USD" ? "$" : ""}
                      {Number(walletBalance.balance).toFixed(2)}
                      {walletBalance.currency !== "USD" && ` ${walletBalance.currency}`}
                    </span>
                  </div>
                  {walletShortfall && (
                    <>
                      <p className="mt-2 text-xs">
                        {t("operator.registerKit.step3.walletShortfall", {
                          defaultValue:
                            "Not enough balance for this tier. Pay with card to continue.",
                        })}
                      </p>
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => setPaymentMethod("flutterwave")}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                            theme === "dark"
                              ? "bg-blue-900/40 hover:bg-blue-900/60 text-blue-200"
                              : "bg-blue-100 hover:bg-blue-200 text-blue-900"
                          }`}
                        >
                          {t("operator.registerKit.step3.payCardInstead", { defaultValue: "Pay with Card Instead" })}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Summary — fee + tax = total, straight from the backend's exact values. */}
          {selectedTierInfo && selectedTierInfo.fee && (
            <div className={`rounded-2xl border p-6 mb-8 ${theme === "dark" ? "bg-[#111] border-gray-800" : "bg-white border-gray-200"}`}>
              <div className="flex items-center justify-between mb-3">
                <p className={`text-xs font-bold uppercase tracking-wider ${theme === "dark" ? "text-gray-500" : "text-gray-500"}`}>
                  {t("operator.registerKit.step3.summary", { defaultValue: "Order summary" })}
                </p>
                {countryCode && (
                  <span className={`text-xs font-medium uppercase ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}>
                    {countryCode}
                  </span>
                )}
              </div>
              <div className="mb-3">
                <p className={`text-sm font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                  {selectedTierInfo.label || selectedTier} {t("operator.registerKit.step3.kitSuffix", { defaultValue: "kit" })}
                </p>
                <p className={`text-xs ${theme === "dark" ? "text-gray-500" : "text-gray-500"}`}>
                  {t("operator.registerKit.step3.activationFee", { defaultValue: "One-time activation fee" })}
                </p>
              </div>

              <div className={`space-y-1.5 text-sm ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}>
                <div className="flex justify-between">
                  <span>{t("operator.registerKit.step3.feeLine", { defaultValue: "Activation fee" })}</span>
                  <span className="font-mono">{formatDecimalString(selectedTierInfo.fee.fee, selectedTierInfo.fee.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t("operator.registerKit.step3.taxLine", { defaultValue: "Tax" })}</span>
                  <span className="font-mono">{formatDecimalString(selectedTierInfo.fee.tax, selectedTierInfo.fee.currency)}</span>
                </div>
                <div className={`flex justify-between items-baseline pt-2 mt-1 border-t ${theme === "dark" ? "border-gray-800" : "border-gray-200"}`}>
                  <span className={`font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    {t("operator.registerKit.step3.totalLine", { defaultValue: "Total" })}
                  </span>
                  <span className={`text-2xl font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    {formatDecimalString(selectedTierInfo.fee.total, selectedTierInfo.fee.currency)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={() => navigate("/operator/registerKitConnectivity")}
              disabled={loading || submitting || verifying}
              className={`px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                theme === "dark" ? "text-gray-300 hover:bg-gray-800" : "text-gray-700 hover:bg-gray-200"
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              {t("operator.registerKit.step3.back", { defaultValue: "Back" })}
            </button>

            <button
              onClick={handleSubmit}
              disabled={loading || submitting || verifying || !tiers || !selectedTierInfo?.fee}
              className="px-10 py-4 bg-[#2E5A88] hover:bg-[#244a73] text-white font-bold rounded-xl transition-all flex items-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading || submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {paymentMethod === "wallet"
                    ? t("operator.registerKit.step3.processing", { defaultValue: "Processing..." })
                    : t("operator.registerKit.step3.redirecting", { defaultValue: "Redirecting..." })}
                </>
              ) : (
                <>
                  {paymentMethod === "wallet"
                    ? t("operator.registerKit.step3.payAndSubmit", {
                        defaultValue: "Pay & Submit for Approval",
                      })
                    : t("operator.registerKit.step3.payWithCard", {
                        defaultValue: "Pay with Card",
                      })}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
